import { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, Clock, Users, Bot, CheckCircle, AlertTriangle, Zap, Timer, Shield, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/atendimento`;
const headers = () => ({Authorization: `Bearer ${localStorage.getItem('recupnet_token')}`});

export default function AtendimentoDashboard() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/dashboard`, {headers: headers()}).then(r => setD(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="rn-spinner" style={{width:28,height:28}}/></div>;

  return (
    <div className="p-6 space-y-6 overflow-y-auto" style={{maxHeight:'100vh'}}>
      <div>
        <h1 className="font-display text-2xl font-semibold">Central de Atendimento</h1>
        <p className="text-sm text-white/40">Metricas operacionais em tempo real</p>
      </div>

      {/* KPIs Row 1 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card icon={MessageSquare} label="Em aberto" value={d?.openTotal} />
        <Card icon={Bot} label="Aguardando IA" value={d?.waitingAI} color="green" />
        <Card icon={Clock} label="Fila Humano" value={d?.waitingHuman} color="warning" />
        <Card icon={Zap} label="Em andamento" value={d?.inProgress} color="green" />
        <Card icon={CheckCircle} label="Fechados hoje" value={d?.closedToday} />
        <Card icon={Users} label="Operadores" value={`${d?.operatorsOnline||0}/${d?.operatorsTotal||0}`} />
      </div>

      {/* Rates + SLA + Times */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <RateCard label="Resolvidos IA" value={`${d?.aiResolvedRate||0}%`} color="green" />
        <RateCard label="Resolvidos Humano" value={`${d?.humanResolvedRate||0}%`} />
        <RateCard label="Taxa Transbordo" value={`${d?.transbordRate||0}%`} color="warning" />
        <RateCard label="TMA" value={`${d?.tmaMinutes||0} min`} sub="Tempo medio atendimento" />
        <RateCard label="TME" value={`${d?.tmeMinutes||0} min`} sub="Tempo medio espera" />
      </div>

      {/* SLA Alerts */}
      {(d?.slaBreached > 0 || d?.slaAtRisk > 0) && (
        <div className="flex gap-3">
          {d?.slaBreached > 0 && (
            <div className="flex-1 p-3 rounded-xl border" style={{background:'rgba(211,47,47,0.08)', borderColor:'rgba(211,47,47,0.25)'}}>
              <div className="flex items-center gap-2"><AlertTriangle size={16} style={{color:'#D32F2F'}}/><span className="text-sm font-semibold text-red-400">{d.slaBreached} SLA estourado(s)</span></div>
            </div>
          )}
          {d?.slaAtRisk > 0 && (
            <div className="flex-1 p-3 rounded-xl border" style={{background:'rgba(249,168,37,0.08)', borderColor:'rgba(249,168,37,0.25)'}}>
              <div className="flex items-center gap-2"><Timer size={16} style={{color:'var(--rn-warning)'}}/><span className="text-sm font-semibold text-yellow-400">{d.slaAtRisk} em risco de SLA</span></div>
            </div>
          )}
        </div>
      )}

      {/* Operator Performance + Queue Volume */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass-card">
          <h3 className="text-sm font-semibold text-white/60 mb-3 flex items-center gap-2"><Users size={16}/> Desempenho por Operador</h3>
          <div className="space-y-2">
            {d?.operatorPerformance?.map(op => (
              <div key={op.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.02]">
                <div className={`w-2 h-2 rounded-full ${op.status==='online'?'bg-[var(--rn-green)]':op.status==='busy'?'bg-[var(--rn-warning)]':'bg-white/20'}`}/>
                <span className="text-xs text-white/80 flex-1">{op.name}</span>
                <span className="text-[10px] text-white/40">{op.active}/{op.max} ativos</span>
                <span className="text-[10px] badge-success">{op.resolved} resolvidos</span>
              </div>
            ))}
          </div>
        </div>
        <div className="glass-card">
          <h3 className="text-sm font-semibold text-white/60 mb-3 flex items-center gap-2"><TrendingUp size={16}/> Volume por Fila</h3>
          <div className="space-y-2">
            {d?.queueVolume?.map(q => (
              <div key={q.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.02]">
                <span className="text-xs text-white/80 flex-1">{q.name}</span>
                <span className="text-[10px] badge-warning">{q.waiting} fila</span>
                <span className="text-[10px] badge-success">{q.active} ativos</span>
                <span className="text-[10px] badge-neutral">{q.closed} fechados</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Categories */}
      {d?.categories?.length > 0 && (
        <div className="glass-card">
          <h3 className="text-sm font-semibold text-white/60 mb-3">Categorias</h3>
          <div className="flex flex-wrap gap-2">
            {d.categories.map(c => (
              <div key={c.name} className="px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10">
                <span className="text-xs text-white/70">{c.name}</span>
                <span className="text-xs font-bold ml-2" style={{color:'var(--rn-green)'}}>{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Card({icon:Icon, label, value, color}) {
  const c = color==='green'?'var(--rn-green)':color==='warning'?'var(--rn-warning)':'rgba(255,255,255,0.35)';
  return (<div className="glass-card !p-3.5"><Icon size={16} style={{color:c}} className="mb-1.5"/><p className="font-display text-xl font-semibold tabular-nums">{value??0}</p><p className="text-[10px] text-white/40 mt-0.5">{label}</p></div>);
}
function RateCard({label, value, color, sub}) {
  const c = color==='green'?'var(--rn-green)':color==='warning'?'var(--rn-warning)':'rgba(255,255,255,0.6)';
  return (<div className="glass-card !p-3.5 text-center"><p className="font-display text-xl font-bold" style={{color:c}}>{value}</p><p className="text-[10px] text-white/40 mt-0.5">{label}</p>{sub && <p className="text-[8px] text-white/20">{sub}</p>}</div>);
}
